import Footer from "./Footer";
import { useActiveChat } from "../context/ActiveChatContext";
import Header from "./Header";
import { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import AdvertisedEventBanner from "../components/AdvertisedEventBanner";
import ReopenBannerButton from "../components/ReopenBannerButton";
import { useAdBanner } from "../context/AdBannerContext";
import { useAdvertiseBanners } from "../features/advertise/api/hooks";
import BottomNavBar from "../components/BottomNavBar";
import { useAuth } from "../features/auth/hooks/useAuth";
import { CALENDAR_ROUTE, EVENTS_ROUTE, LANDING_ROUTE } from "../routes";
import PopupBannerModal, { isBannerPopupEligible } from "../components/PopupBannerModal";
import ProfilePhotoBanner from "../features/user/components/ProfilePhotoBanner";
//import SupportBanner from "../components/SupportBanner";

export default function PublicLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { activeChat } = useActiveChat(); // Add this line to get active chat state
  const { isBannerVisible, setHasBanner, closeBanner, setActiveBannerIds } = useAdBanner();
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const pagesWithBottomNav = [EVENTS_ROUTE, CALENDAR_ROUTE];
  const showBottomNav = isAuthenticated || pagesWithBottomNav.some(path => location.pathname.startsWith(path));
  const showFooterOnMobile = location.pathname === LANDING_ROUTE;
  
  // Fetch advertise banners from backend
  const { banners } = useAdvertiseBanners();

  // Check if viewport is mobile-sized
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is Tailwind's md breakpoint
    };

    // Check initially
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Use h-screen for chat layout to constrain desktop height, min-h-screen for other pages
  const isOnChatPage = location.pathname === "/chats";
  
  // Only hide header/footer when ALL conditions are true:
  // 1. There is an active chat
  // 2. We're on mobile
  // 3. We're actually on the chat page
  const shouldHideHeaderFooter = activeChat && isMobile && isOnChatPage;
  const shouldUseFullHeight =
    shouldHideHeaderFooter || (isOnChatPage && activeChat);

  // Transform backend banners to frontend format and filter active ones
  // Use useMemo to prevent infinite re-renders
  const activeEvents = useMemo(() => {
    const now = new Date();
    return banners
      .filter(banner => banner.type !== "POPUP") // POPUP banners are handled separately
      .map(banner => ({
        id: banner.id,
        title: banner.title,
        imageUrl: banner.imageUrl,
        linkUrl: banner.linkUrl,
        organizerName: banner.organizerName,
        organizerImage: banner.organizerImage,
        startDate: banner.startDate,
        expirationDate: banner.expirationDate,
      }))
      .filter(event => {
        const startDate = new Date(event.startDate);
        const expirationDate = new Date(event.expirationDate);
        return now >= startDate && now <= expirationDate;
      });
  }, [banners]);

  // Track which popup the user has dismissed in this session.
  const [dismissedPopupId, setDismissedPopupId] = useState<string | null>(null);

  // Compute the first eligible POPUP banner (date-active + localStorage cooldown).
  const activePopupBanner = useMemo(() => {
    const now = new Date();
    return banners.find(banner => {
      if (banner.type !== "POPUP") return false;
      if (banner.id === dismissedPopupId) return false;
      const start = new Date(banner.startDate);
      const end = new Date(banner.expirationDate);
      if (now < start || now > end) return false;
      return isBannerPopupEligible(banner.id);
    }) ?? null;
  }, [banners, dismissedPopupId]);

  // Get active banner IDs as a stable string for dependency tracking
  const activeEventIdsString = useMemo(() => {
    return activeEvents.map(e => e.id).sort().join(',');
  }, [activeEvents]);

  // Update context about banner existence and active banner IDs
  // Only update when the IDs string actually changes
  useEffect(() => {
    const hasActiveBanners = activeEvents.length > 0;
    const activeIds = activeEvents.map(e => e.id);
  
    setHasBanner(hasActiveBanners);
    
    // Only update IDs if they actually changed
    if (activeEventIdsString !== '') {
      setActiveBannerIds(activeIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEventIdsString]); // Only re-run when the IDs string changes

  // Check if we should show the full banner or the reopen button
  const hasActiveBanners = !shouldHideHeaderFooter && activeEvents.length > 0;
  const showFullBanner = hasActiveBanners && isBannerVisible;
  const showReopenButton = hasActiveBanners && !isBannerVisible;
  
  // Calculate header position based on banner visibility
  // When reopen button is shown, header goes directly below it (no gap)
  const headerTopPosition = showFullBanner ? 'top-[128px] md:top-[160px]' : showReopenButton ? 'top-[40px]' : 'top-0';

  // Ref to the fixed header wrapper so we can set the CSS var --main-header-height
  const headerWrapperRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = headerWrapperRef.current;
    if (!el) return;

    const setMainHeight = () => {
      try {
        const h = Math.round(el.getBoundingClientRect().height) || 72;
        document.documentElement.style.setProperty('--main-header-height', `${h}px`);
        // Ensure banner-height CSS var accounts for the reopen button case as well
        // so that the events header (which uses --events-header-top = --main-header-height + --banner-height)
        // sits below both the header and any top banner or reopen-button.
        try {
          if (showFullBanner) {
            // when full banner is visible we reserve the large banner height
            document.documentElement.style.setProperty('--banner-height', '128px');
          } else if (showReopenButton) {
            // when the small reopen button is visible reserve 40px
            document.documentElement.style.setProperty('--banner-height', '40px');
          } else {
            document.documentElement.style.setProperty('--banner-height', '0px');
          }
        } catch (e) {/* ignore */}
      } catch (e) {
        // ignore
      }
    };

    // initial
    setMainHeight();

    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new (window as any).ResizeObserver(() => setMainHeight());
      try { if (ro) ro.observe(el); } catch (e) { /* ignore */ }
    } else {
      window.addEventListener('resize', setMainHeight);
    }

    return () => { if (ro) try { ro.disconnect(); } catch (e) {} else window.removeEventListener('resize', setMainHeight); };
  }, [showFullBanner, showReopenButton]);

  return (
    <div
      className={`flex flex-col overflow-x-hidden w-full ${
        shouldUseFullHeight ? "h-screen" : "min-h-screen"
      }`}
    >
      {/* Advertised Events Banner - at the very top */}
      {showFullBanner && (
        <AdvertisedEventBanner 
          events={activeEvents} 
          onClose={closeBanner}
        />
      )}

      {/* Reopen Banner Button - shown when banner is closed but there are active banners */}
      {showReopenButton && <ReopenBannerButton />}
      
      {!shouldHideHeaderFooter && (
        <div ref={headerWrapperRef} className={`fixed ${headerTopPosition} left-0 right-0 z-40 bg-white border-b border-gray-200`}>
          <Header />
        </div>
      )}
      {/* Show SupportBanner on all pages except chat */}
      {/* {!shouldHideHeaderFooter && !isOnChatPage && <SupportBanner />} */}

      {/* Profile-photo nudge banner — fixed (not sticky: PublicLayout's root
          div below sets overflow-x-hidden, which per spec forces overflow-y
          to compute as `auto` too, turning this into a sticky-positioning
          container that never actually scrolls — position:sticky silently
          stops sticking to the viewport inside it. `fixed` is immune to
          ancestor overflow, matching how AdvertisedEventBanner/Header are
          also `fixed` for the same reason.) Suppressed while the advertise
          popup is showing so the two never compete for attention. */}
      {!shouldHideHeaderFooter && !isOnChatPage && !activePopupBanner && (
        <ProfilePhotoBanner />
      )}

      {/* Spacer to prevent content from being hidden under fixed banner and header */}
      {showFullBanner && (
        <div className="h-[128px] md:h-[160px]"></div>
      )}
      {showReopenButton && (
        <div className="h-[40px]"></div>
      )}
      {!shouldHideHeaderFooter && (
        // Uses the live measured height (--main-header-height, set by the
        // ResizeObserver above) instead of a hardcoded value — the header's
        // actual height varies with viewport width (e.g. nav wrapping), and
        // a stale spacer here left content (visibly, our colored banners)
        // clipped a few px under the fixed header at some widths.
        <div style={{ height: "var(--main-header-height)" }}></div>
      )}
      {!shouldHideHeaderFooter && !isOnChatPage && !activePopupBanner && (
        <div style={{ height: "var(--profile-photo-banner-height)" }}></div>
      )}

      <main
        className={`flex-1 min-h-0 ${shouldHideHeaderFooter ? "py-0 h-full" : "py-2"} ${
          isOnChatPage && activeChat && !isMobile ? "overflow-hidden" : ""
        } ${isOnChatPage && !isMobile ? "overflow-y-hidden" : ""}`}
      >
        <div
          className={`${
            shouldHideHeaderFooter ? "px-0 h-full" : "px-4 sm:px-6 lg:px-8"
          } ${isOnChatPage && activeChat && !isMobile ? "h-full" : ""}`}
        >
          {children}
        </div>
      </main>
      {!shouldHideHeaderFooter && (!isMobile || showFooterOnMobile) && <Footer />}
      {showBottomNav && <BottomNavBar />}
      {/* Popup Banner Modal — shown over all content */}
      {activePopupBanner && (
        <PopupBannerModal
          banner={activePopupBanner}
          onDismiss={() => setDismissedPopupId(activePopupBanner.id)}
        />
      )}
    </div>
  );
}
